import styled from "styled-components";
import { useStockCycleDatasValueContextHook } from "../../contexts/StockCycleDatasContext";
import { CustomRatioImage } from "../../../../../components/image/CustomRatioImage";
import {
    useSettingVariablesActionsContextHook,
    useSettingVariablesValueContextHook,
} from "../../contexts/SettingVariablesContext";
import { Link } from "react-router-dom";
import CustomBlockButton from "../../../../../components/button/CustomBlockButton";
import { useEffect, useState } from "react";
import { ModalViewMemo } from "./modals/modal-view-memo";
import { useCustomFetcher } from "../../fetcher/useCustomFetcher";
import { ModalViewProductAnalytics } from "./modals/modal-view-product-analytics";
import { customToast } from "../../../../../components/toast/custom-react-toastify/v1";
import {
    ArrowDown,
    ArrowDownUp,
    ArrowUp,
    Copy,
    LucideCircleAlert,
    LucideLink,
} from "lucide-react";
import { useSheetItemListValueContextHook } from "../../contexts/SheetItemListContext";
import _ from "lodash";
import { ModalViewAlreadyPurchasedQty } from "./modals/modal-view-already-purchased-qty";
import {
    CustomPopover,
    useCustomPopoverHook,
} from "../../../../../components/popover/CustomPopover";
import { OutlierWarningPopoverFragment } from "./fragments/outlier-warning-popover-fragment";
import { usePurchaseInfosQuery } from "../../Data/purchaseInfosReactQuery/PurchaseInfosReactQuery";
import { ModalViewPurchaseMemo } from "./modals/modal-view-purchase-memo";
import {
    usePurchaseInfosActionsContextHook,
    usePurchaseInfosValueContextHook,
} from "../../contexts/PurchaseInfosContext";
import { ModalViewPurchase } from "./modals/modal-view-purchase";
import { CALCULATOR_SERVICE } from "./service/calculatorService";
import { useSelectedShoppingCartStageValueContextHook } from "../../contexts/SelectedShoppingCartStageContext";
import { useShoppingCartItemsQuery } from "../../Data/shoppingCartItemsReactQuery/ShoppingCartItemsReactQuery";
import CustomSmallButton from "../../../../../components/button/CustomSmallButton/CustomSmallButton";
import { useOrderQtyListQuery } from "../../Data/orderQtyListReactQuery/OrderQtyListReactQuery";
import { useAnalysisVersionValueContextHook } from "../../contexts/AnalysisVersionContext";
import { ANALYSIS_VERSIONS } from "../../variables/analysisVersions";
import { STOCK_CYCLE_DATA_SERVICE } from "../../service/stockCycleDataService";

const StyledContainer = styled.div`
    padding: 10px;
`;

const TableContainer = styled.div`
    overflow-x: auto;
    margin: 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    width: 100%;
    max-height: 500px;
    border: 1px solid #efefef;
`;

const StyledTable = styled.table`
    table-layout: auto; /* 내용 길이에 맞춰 열 너비 자동 */
    white-space: nowrap; /* 한 줄로 표시하여 열이 자동 확장 */
    border-collapse: collapse;
    background-color: #ffffff;

    /* width: 100%;  <-- 필요한 경우 제거하거나 조정
     min-width: 800px; <-- 원한다면 유지 (너무 좁아지지 않게)
  */
`;

const TableHeader = styled.thead`
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #f8f9fa;
`;

const TableHeaderCell = styled.th`
    padding: 16px;
    text-align: left;
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #dee2e6;
    white-space: nowrap;
    font-size: 12px;
`;

const TableRow = styled.tr`
    &:nth-child(even) {
        background-color: #f8f9fa;
    }

    &:hover {
        background-color: #e9ecef;
    }
`;

const TableCell = styled.td`
    padding: 16px;
    color: #495057;
    border-bottom: 1px solid #dee2e6;
    font-size: 12px;
    font-weight: 600;

    .thumbnail {
        width: 30px;
        height: 30px;
    }

    .danger-badge {
        display: inline-block;
        // margin-top: 4px;
        background-color: var(--redColor1);
        color: white;
        padding: 2px 4px;
        border-radius: 3px;
        font-size: 12px;
        font-weight: 600;
    }

    .view-memo-button {
        width: fit-content;
        padding: 0 16px;
        height: 30px;
        border-radius: 3px;
        font-size: 12px;
        background-color: #333;
        color: #fff;
        font-weight: 700;
        border: none;
    }

    .copy-span {
        cursor: pointer;
        &:hover {
            color: #007bff !important;
        }
    }
`;

const SAFETY_STOCK_QTY_MULTIPLIER = 5; // Safety stock is set to 5 times the daily release quantity

export function WegetTable(props) {
    const settingVariablesValueContextHook =
        useSettingVariablesValueContextHook();
    const settingVariables = settingVariablesValueContextHook.settingVariables;

    const analysisVersionValueContextHook = useAnalysisVersionValueContextHook();
    const isV3 =
        analysisVersionValueContextHook.analysisVersion === ANALYSIS_VERSIONS.V3;

    const stockCycleDatasValueContextHook =
        useStockCycleDatasValueContextHook();
    const stockCycleDatas = stockCycleDatasValueContextHook.stockCycleDatas;
    const productOptionsDetailList = stockCycleDatas?.productOptionsDetailList;

    const sheetItemListValueContextHook = useSheetItemListValueContextHook();
    const sheetItemList = sheetItemListValueContextHook?.sheetItemList;
    const alreadyPurchasedQtyList =
        CALCULATOR_SERVICE.getAlreadyPurchasedQtyList(sheetItemList);

    const selectedShoppingCartStageValueContextHook =
        useSelectedShoppingCartStageValueContextHook();
    const selectedShoppingCartStage =
        selectedShoppingCartStageValueContextHook?.selectedShoppingCartStage;

    const customPopoverHook = useCustomPopoverHook();

    const [viewPurchaseMemosModalOpen, setViewPurchaseMemosModalOpen] =
        useState(false);
    const [viewMemoModalOpen, setViewMemoModalOpen] = useState(false);
    const [viewProductAnalyticsModalOpen, setViewProductAnalyticsModalOpen] =
        useState(false);
    const [
        viewAlreadyPurchasedQtyModalOpen,
        setViewAlreadyPurchasedQtyModalOpen,
    ] = useState(false);
    const [viewPurchaseModalOpen, setViewPurchaseModalOpen] = useState(false);

    const [targetProductOptionDetail, setTargetProductOptionDetail] =
        useState(null);
    const [targetAlreadyPurchasedQtyData, setTargetAlreadyPurchasedQtyData] =
        useState(null);

    const { data: purchaseInfosData } = usePurchaseInfosQuery();
    const purchaseInfos = purchaseInfosData?.data?.content;
    const purchaseInfosActionsContextHook =
        usePurchaseInfosActionsContextHook();

    const {
        data: shoppingCartItemsData,
        isLoading: shoppingCartItemsDataIsLoading,
    } = useShoppingCartItemsQuery({
        shoppingCartStageId: selectedShoppingCartStage?._id,
    });
    const shoppingCartItemCollections = shoppingCartItemsData?.data?.content;

    const { data: orderQtyListData, isLoading: orderQtyListDataIsLoading } =
        useOrderQtyListQuery({
            productOptionIds: productOptionsDetailList?.map(
                (item) => item?.productOptionDto?.id,
            ),
        });
    const orderQtyList = orderQtyListData?.data?.content;

    const [sortableReleasePerDays, setSortableReleasePerDays] = useState(null); // null, ASC, DESC

    useEffect(() => {
        if (purchaseInfosData?.data?.content) {
            purchaseInfosActionsContextHook.purchaseInfos.onSet(
                purchaseInfosData?.data?.content,
            );
        }
    }, [purchaseInfosData]);

    const handleViewMemoModalClose = () => {
        setTargetProductOptionDetail(null);
        setViewMemoModalOpen(false);
    };

    const handleViewProductAnalyticsModalOpen = (productOptionDetail) => {
        setTargetProductOptionDetail(productOptionDetail);
        setViewProductAnalyticsModalOpen(true);
    };

    const handleViewProductAnalyticsModalClose = () => {
        setTargetProductOptionDetail(null);
        setViewProductAnalyticsModalOpen(false);
    };

    const handleCopyText = (text) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                customToast.success("복사되었습니다.");
            })
            .catch((error) => {
                customToast.error("복사 실패:", error);
            });
    };

    const handleCopyOptionCodes = () => {
        const filteredProductOptions = productOptionsDetailList
            ?.filter((productOptionDetail) => {
                if (settingVariables?.hideNoOrderFlag) {
                    return productOptionDetail?.iqrData?.releaseQty > 0;
                }
                return true;
            })
            ?.filter((productOptionDetail) => {
                if (settingVariables?.viewOnlyUnderLeadTimeFlag) {
                    const daysOfCanRelease =
                        CALCULATOR_SERVICE.getDaysOfCanRelease({
                            stockQty:
                                productOptionDetail?.inventoryStockDto
                                    ?.stockUnit,
                            releaseQtyPerDay:
                                productOptionDetail?.iqrData?.releaseQtyPerDay,
                        });
                    return daysOfCanRelease < settingVariables.daysOfLeadTime;
                }
                return true;
            });
        const optionCodes = filteredProductOptions
            ?.map(
                (productOptionDetail) =>
                    productOptionDetail?.productOptionDto?.code,
            )
            .join("\n");
        navigator.clipboard
            .writeText(optionCodes)
            .then(() => {
                customToast.success("옵션코드가 복사되었습니다.");
            })
            .catch((error) => {
                customToast.error("복사 실패:", error);
            });
    };

    // basedOnActualQty 가 true 면 보정(IQR)값 대신 실제 일평균 주문량으로 계산한다.
    const handleCopyRecommendPurchaseQtyList = (basedOnActualQty = false) => {
        const filteredProductOptions = productOptionsDetailList
            ?.filter((productOptionDetail) => {
                if (settingVariables?.hideNoOrderFlag) {
                    return productOptionDetail?.iqrData?.releaseQty > 0;
                }
                return true;
            })
            ?.filter((productOptionDetail) => {
                if (settingVariables?.viewOnlyUnderLeadTimeFlag) {
                    const daysOfCanRelease =
                        CALCULATOR_SERVICE.getDaysOfCanRelease({
                            stockQty:
                                productOptionDetail?.inventoryStockDto
                                    ?.stockUnit,
                            releaseQtyPerDay:
                                productOptionDetail?.iqrData?.releaseQtyPerDay,
                        });
                    return daysOfCanRelease < settingVariables.daysOfLeadTime;
                }
                return true;
            });

        const recommendPurchaseQtyList = filteredProductOptions
            ?.map((productOptionDetail) => {
                const releaseQtyPerDay = basedOnActualQty
                    ? productOptionDetail?.releaseQtyPerDay
                    : productOptionDetail?.iqrData?.releaseQtyPerDay;
                const safetyStockQty = releaseQtyPerDay
                    ? (
                          releaseQtyPerDay * SAFETY_STOCK_QTY_MULTIPLIER
                      ).toFixed(2)
                    : 0;
                const [
                    purchasedQty,
                    deliveryQty,
                    arrivedQty,
                    documentQty,
                    shippingQty,
                ] = alreadyPurchasedQtyList?.map((alreadyPurchasedQty) => {
                    let quantity = 0;
                    const targetData = alreadyPurchasedQty?.summedArray?.find(
                        (r) =>
                            r.optionCode ===
                            productOptionDetail?.productOptionDto?.code,
                    );
                    if (targetData) {
                        quantity = targetData?.quantity;
                    }
                    return quantity;
                });

                const totalAlreadyPurchasedQty =
                    purchasedQty +
                    deliveryQty +
                    arrivedQty +
                    documentQty +
                    shippingQty;

                const recommendPurchaseQty =
                    CALCULATOR_SERVICE.getRecommendPurchaseQty({
                        stockQty:
                            productOptionDetail?.inventoryStockDto?.stockUnit,
                        releaseQtyPerDay: releaseQtyPerDay,
                        targetDOS: settingVariables.targetDOS,
                        daysOfLeadTime: settingVariables.daysOfLeadTime,
                        safetyStockQty: safetyStockQty,
                        totalAlreadyPurchasedQty: totalAlreadyPurchasedQty,
                    });

                return `${recommendPurchaseQty > 0 ? recommendPurchaseQty : 0}`;
            })
            .join("\n");

        navigator.clipboard
            .writeText(recommendPurchaseQtyList)
            .then(() => {
                customToast.success(
                    `${basedOnActualQty ? "실주문 기준 " : ""}추천구매수량이 복사되었습니다.`,
                );
            })
            .catch((error) => {
                customToast.error("복사 실패:", error);
            });
    };

    let filteredProductOptionDetailList = productOptionsDetailList
        ?.filter((productOptionDetail) => {
            if (settingVariables?.hideNoOrderFlag) {
                return productOptionDetail?.iqrData?.releaseQty > 0;
            }
            return true;
        })
        ?.filter((productOptionDetail) => {
            if (settingVariables?.viewOnlyUnderLeadTimeFlag) {
                const daysOfCanRelease = CALCULATOR_SERVICE.getDaysOfCanRelease(
                    {
                        stockQty:
                            productOptionDetail?.inventoryStockDto?.stockUnit,
                        releaseQtyPerDay:
                            productOptionDetail?.iqrData?.releaseQtyPerDay,
                    },
                );
                return daysOfCanRelease < settingVariables.daysOfLeadTime;
            }
            return true;
        });

    if (sortableReleasePerDays) {
        filteredProductOptionDetailList = filteredProductOptionDetailList.sort(
            (a, b) => {
                const daysOfCanReleaseA =
                    CALCULATOR_SERVICE.getDaysOfCanRelease({
                        stockQty: a?.inventoryStockDto?.stockUnit,
                        releaseQtyPerDay: a?.iqrData?.releaseQtyPerDay,
                    });

                const daysOfCanReleaseB =
                    CALCULATOR_SERVICE.getDaysOfCanRelease({
                        stockQty: b?.inventoryStockDto?.stockUnit,
                        releaseQtyPerDay: b?.iqrData?.releaseQtyPerDay,
                    });

                return sortableReleasePerDays === "DESC"
                    ? daysOfCanReleaseB - daysOfCanReleaseA
                    : daysOfCanReleaseA - daysOfCanReleaseB;
            },
        );
    }

    return (
        <>
            <StyledContainer>
                <TableContainer>
                    <StyledTable>
                        <TableHeader>
                            <tr>
                                <TableHeaderCell>상품이미지</TableHeaderCell>
                                <TableHeaderCell>상품정보</TableHeaderCell>
                                <TableHeaderCell>옵션정보</TableHeaderCell>
                                <TableHeaderCell>
                                    <div className="flex gap-1 items-center">
                                        옵션코드{" "}
                                        <Copy
                                            size={12}
                                            onClick={() =>
                                                handleCopyOptionCodes()
                                            }
                                            style={{ cursor: "pointer" }}
                                        />
                                    </div>
                                </TableHeaderCell>
                                <TableHeaderCell>재고수량</TableHeaderCell>
                                <TableHeaderCell>안전재고</TableHeaderCell>
                                <TableHeaderCell style={{ fontSize: "10px" }}>
                                    분석기간내 주문량 / {isV3 ? "보정" : "IQR"}
                                </TableHeaderCell>
                                <TableHeaderCell style={{ fontSize: "10px" }}>
                                    일평균 주문량 / {isV3 ? "보정" : "IQR"}
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    <div className="flex gap-1 items-center">
                                        출고가능기간
                                        {sortableReleasePerDays === null && (
                                            <ArrowDownUp
                                                size={12}
                                                onClick={() =>
                                                    setSortableReleasePerDays(
                                                        "DESC",
                                                    )
                                                }
                                                style={{ cursor: "pointer" }}
                                            />
                                        )}
                                        {sortableReleasePerDays === "DESC" && (
                                            <ArrowDown
                                                size={12}
                                                onClick={() =>
                                                    setSortableReleasePerDays(
                                                        "ASC",
                                                    )
                                                }
                                                style={{ cursor: "pointer" }}
                                            />
                                        )}
                                        {sortableReleasePerDays === "ASC" && (
                                            <ArrowUp
                                                size={12}
                                                onClick={() =>
                                                    setSortableReleasePerDays(
                                                        null,
                                                    )
                                                }
                                                style={{ cursor: "pointer" }}
                                            />
                                        )}
                                    </div>
                                </TableHeaderCell>
                                <TableHeaderCell>이미 구매됨</TableHeaderCell>
                                <TableHeaderCell style={{ fontSize: "10px" }}>
                                    <div className="flex gap-1 items-center">
                                        추천구매수량 ({isV3 ? "보정" : "IQR"})
                                        <Copy
                                            size={12}
                                            onClick={() =>
                                                handleCopyRecommendPurchaseQtyList()
                                            }
                                            style={{ cursor: "pointer" }}
                                        />
                                    </div>
                                </TableHeaderCell>
                                <TableHeaderCell style={{ fontSize: "10px" }}>
                                    <div className="flex gap-1 items-center">
                                        추천구매수량 (실주문)
                                        <Copy
                                            size={12}
                                            onClick={() =>
                                                handleCopyRecommendPurchaseQtyList(
                                                    true,
                                                )
                                            }
                                            style={{ cursor: "pointer" }}
                                        />
                                    </div>
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    신규/보류 발주수량
                                </TableHeaderCell>
                                <TableHeaderCell>장바구니 수량</TableHeaderCell>
                                <TableHeaderCell>장바구니 담기</TableHeaderCell>
                                <TableHeaderCell>구매정보</TableHeaderCell>
                            </tr>
                        </TableHeader>
                        <tbody>
                            {filteredProductOptionDetailList?.map(
                                (productOptionDetail, rowIndex) => {
                                    const safetyStockQty = productOptionDetail
                                        ?.iqrData?.releaseQtyPerDay
                                        ? (
                                              productOptionDetail?.iqrData
                                                  ?.releaseQtyPerDay *
                                              SAFETY_STOCK_QTY_MULTIPLIER
                                          ).toFixed(2)
                                        : 0;
                                    const daysOfCanRelease =
                                        CALCULATOR_SERVICE.getDaysOfCanRelease({
                                            stockQty:
                                                productOptionDetail
                                                    ?.inventoryStockDto
                                                    ?.stockUnit,
                                            releaseQtyPerDay:
                                                productOptionDetail?.iqrData
                                                    ?.releaseQtyPerDay,
                                        });
                                    const weeksOfCanRelease = Math.floor(
                                        daysOfCanRelease / 7,
                                    );
                                    const [
                                        purchasedQty,
                                        deliveryQty,
                                        arrivedQty,
                                        documentQty,
                                        shippingQty,
                                    ] = alreadyPurchasedQtyList?.map(
                                        (alreadyPurchasedQty) => {
                                            let quantity = 0;
                                            const targetData =
                                                alreadyPurchasedQty?.summedArray?.find(
                                                    (r) =>
                                                        r.optionCode ===
                                                        productOptionDetail
                                                            ?.productOptionDto
                                                            ?.code,
                                                );
                                            if (targetData) {
                                                quantity = targetData?.quantity;
                                            }
                                            return quantity;
                                        },
                                    );

                                    const totalAlreadyPurchasedQty =
                                        purchasedQty +
                                        deliveryQty +
                                        arrivedQty +
                                        documentQty +
                                        shippingQty;

                                    const recommendPurchaseQty =
                                        CALCULATOR_SERVICE.getRecommendPurchaseQty(
                                            {
                                                stockQty:
                                                    productOptionDetail
                                                        ?.inventoryStockDto
                                                        ?.stockUnit,
                                                releaseQtyPerDay:
                                                    productOptionDetail?.iqrData
                                                        ?.releaseQtyPerDay,
                                                targetDOS:
                                                    settingVariables.targetDOS,
                                                daysOfLeadTime:
                                                    settingVariables.daysOfLeadTime,
                                                safetyStockQty: safetyStockQty,
                                                totalAlreadyPurchasedQty:
                                                    totalAlreadyPurchasedQty,
                                            },
                                        );

                                    // 실제 일평균 주문량(보정 전) 기준 추천구매수량
                                    const actualSafetyStockQty =
                                        productOptionDetail?.releaseQtyPerDay
                                            ? (
                                                  productOptionDetail?.releaseQtyPerDay *
                                                  SAFETY_STOCK_QTY_MULTIPLIER
                                              ).toFixed(2)
                                            : 0;

                                    const actualRecommendPurchaseQty =
                                        CALCULATOR_SERVICE.getRecommendPurchaseQty(
                                            {
                                                stockQty:
                                                    productOptionDetail
                                                        ?.inventoryStockDto
                                                        ?.stockUnit,
                                                releaseQtyPerDay:
                                                    productOptionDetail?.releaseQtyPerDay,
                                                targetDOS:
                                                    settingVariables.targetDOS,
                                                daysOfLeadTime:
                                                    settingVariables.daysOfLeadTime,
                                                safetyStockQty:
                                                    actualSafetyStockQty,
                                                totalAlreadyPurchasedQty:
                                                    totalAlreadyPurchasedQty,
                                            },
                                        );

                                    // 이상치 여부 확인
                                    const isOutlierWarning =
                                        STOCK_CYCLE_DATA_SERVICE.isOutlierWarning(
                                            productOptionDetail,
                                        );

                                    const defaultPurchaseInfo =
                                        purchaseInfos?.find(
                                            (purchaseInfo) =>
                                                purchaseInfo.productOptionId ===
                                                    productOptionDetail
                                                        ?.productOptionDto
                                                        ?.id &&
                                                purchaseInfo?.defaultFlag ===
                                                    true,
                                        );

                                    // 장바구니 구매목록
                                    const targetShoppingCartItemCollections =
                                        shoppingCartItemCollections?.filter(
                                            (item) =>
                                                item.shoppingCartItemDto
                                                    ?.productOptionId ===
                                                productOptionDetail
                                                    ?.productOptionDto?.id,
                                        );
                                    const shoppingCartPurchaseQty =
                                        targetShoppingCartItemCollections?.reduce(
                                            (prev, curr) => {
                                                return (
                                                    prev +
                                                    curr.shoppingCartItemDto
                                                        ?.purchaseQty
                                                );
                                            },
                                            0,
                                        );

                                    let orderQty = orderQtyList?.find(
                                        (item) =>
                                            item.productOptionId ===
                                            productOptionDetail
                                                ?.productOptionDto?.id,
                                    ) || {
                                        newQty: 0,
                                        confirmQty: 0,
                                        completeQty: 0,
                                        holdQty: 0,
                                    };

                                    const orderNewAndHoldQtySum =
                                        orderQty?.newQty + orderQty?.holdQty;

                                    return (
                                        <TableRow key={rowIndex}>
                                            <TableCell>
                                                <div className="thumbnail">
                                                    <CustomRatioImage>
                                                        <CustomRatioImage.Image
                                                            src={
                                                                productOptionDetail
                                                                    ?.productDto
                                                                    ?.thumbnailUri
                                                            }
                                                        />
                                                    </CustomRatioImage>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    onClick={() =>
                                                        handleViewProductAnalyticsModalOpen(
                                                            productOptionDetail,
                                                        )
                                                    }
                                                    style={{
                                                        textDecoration:
                                                            "underline",
                                                        cursor: "pointer",
                                                        fontWeight: "800",
                                                    }}
                                                >
                                                    {
                                                        productOptionDetail
                                                            ?.productDto?.name
                                                    }
                                                </span>{" "}
                                                [
                                                {
                                                    productOptionDetail
                                                        ?.productDto?.productTag
                                                }
                                                ]
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    productOptionDetail
                                                        ?.productOptionDto?.name
                                                }{" "}
                                                [
                                                {
                                                    productOptionDetail
                                                        ?.productOptionDto
                                                        ?.productTag
                                                }
                                                ]
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    className="copy-span"
                                                    onClick={() =>
                                                        handleCopyText(
                                                            productOptionDetail
                                                                ?.productOptionDto
                                                                ?.code,
                                                        )
                                                    }
                                                >
                                                    {
                                                        productOptionDetail
                                                            ?.productOptionDto
                                                            ?.code
                                                    }
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {productOptionDetail
                                                    ?.inventoryStockDto
                                                    ?.stockUnit <= 0 ? (
                                                    <span
                                                        style={{
                                                            color: "var(--redColor1)",
                                                        }}
                                                    >
                                                        {
                                                            productOptionDetail
                                                                ?.inventoryStockDto
                                                                ?.stockUnit
                                                        }
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {
                                                            productOptionDetail
                                                                ?.inventoryStockDto
                                                                ?.stockUnit
                                                        }
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {safetyStockQty}
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    style={{
                                                        color: "#808080",
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    {
                                                        productOptionDetail?.releaseQty
                                                    }
                                                </span>{" "}
                                                /{" "}
                                                <span
                                                    style={{
                                                        color: "var(--blueColor1)",
                                                        fontWeight: "800",
                                                    }}
                                                >
                                                    {
                                                        productOptionDetail
                                                            ?.iqrData
                                                            ?.releaseQty
                                                    }
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1 items-center">
                                                    <div>
                                                        <span
                                                            style={{
                                                                color: "#808080",
                                                                fontWeight:
                                                                    "500",
                                                            }}
                                                        >
                                                            {
                                                                productOptionDetail?.releaseQtyPerDay
                                                            }
                                                        </span>{" "}
                                                        /{" "}
                                                        <span
                                                            style={{
                                                                color: "var(--blueColor1)",
                                                                fontWeight:
                                                                    "800",
                                                            }}
                                                        >
                                                            {
                                                                productOptionDetail
                                                                    ?.iqrData
                                                                    ?.releaseQtyPerDay
                                                            }
                                                        </span>
                                                    </div>
                                                    {isOutlierWarning && (
                                                        <span>
                                                            <LucideCircleAlert
                                                                size={16}
                                                                color={
                                                                    "#ffbf00"
                                                                }
                                                                cursor={
                                                                    "pointer"
                                                                }
                                                                onClick={(e) =>
                                                                    customPopoverHook.openPopover(
                                                                        e,
                                                                        {
                                                                            productOptionDetail:
                                                                                productOptionDetail,
                                                                            recommendPurchaseQty:
                                                                                recommendPurchaseQty,
                                                                            totalAlreadyPurchasedQty:
                                                                                totalAlreadyPurchasedQty,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1 items-center">
                                                    <div>
                                                        {daysOfCanRelease}일 /{" "}
                                                        {weeksOfCanRelease}주
                                                    </div>
                                                    {daysOfCanRelease <
                                                    settingVariables.daysOfLeadTime ? (
                                                        <span className="danger-badge">
                                                            위험
                                                        </span>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    style={{
                                                        textDecoration:
                                                            "underline",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        setTargetAlreadyPurchasedQtyData(
                                                            {
                                                                productOptionCode:
                                                                    productOptionDetail
                                                                        ?.productOptionDto
                                                                        ?.code,
                                                                productName:
                                                                    productOptionDetail
                                                                        ?.productDto
                                                                        ?.name,
                                                                productOptionName:
                                                                    productOptionDetail
                                                                        ?.productOptionDto
                                                                        ?.name,
                                                                productTag:
                                                                    productOptionDetail
                                                                        ?.productDto
                                                                        ?.productTag,
                                                                optionTag:
                                                                    productOptionDetail
                                                                        ?.productOptionDto
                                                                        ?.optionTag,
                                                                purchasedQty:
                                                                    purchasedQty,
                                                                deliveryQty:
                                                                    deliveryQty,
                                                                arrivedQty:
                                                                    arrivedQty,
                                                                documentQty:
                                                                    documentQty,
                                                                shippingQty:
                                                                    shippingQty,
                                                                totalAlreadyPurchasedQty:
                                                                    totalAlreadyPurchasedQty
                                                                        ? totalAlreadyPurchasedQty
                                                                        : 0,
                                                            },
                                                        );
                                                        setViewAlreadyPurchasedQtyModalOpen(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    {totalAlreadyPurchasedQty
                                                        ? totalAlreadyPurchasedQty
                                                        : "0"}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {recommendPurchaseQty > 0 ? (
                                                    <span
                                                        style={{
                                                            color: "var(--greenColor1)",
                                                        }}
                                                        className="copy-span"
                                                        onClick={() =>
                                                            handleCopyText(
                                                                recommendPurchaseQty,
                                                            )
                                                        }
                                                    >
                                                        {recommendPurchaseQty}
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="copy-span"
                                                        onClick={() =>
                                                            handleCopyText("0")
                                                        }
                                                    >
                                                        0
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {actualRecommendPurchaseQty >
                                                0 ? (
                                                    <span
                                                        style={{
                                                            color: "#808080",
                                                        }}
                                                        className="copy-span"
                                                        onClick={() =>
                                                            handleCopyText(
                                                                actualRecommendPurchaseQty,
                                                            )
                                                        }
                                                    >
                                                        {
                                                            actualRecommendPurchaseQty
                                                        }
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="copy-span"
                                                        onClick={() =>
                                                            handleCopyText("0")
                                                        }
                                                    >
                                                        0
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-bold text-blue-500">
                                                    TOTAL:{" "}
                                                    {orderNewAndHoldQtySum}
                                                </div>
                                                <div className="text-[10px] text-gray-500">
                                                    신규: {orderQty?.newQty} /
                                                    보류:{orderQty?.holdQty}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {!selectedShoppingCartStage ? (
                                                    <span
                                                        style={{
                                                            color: "#a0a0a0",
                                                        }}
                                                    >
                                                        스테이지 선택 필요
                                                    </span>
                                                ) : (
                                                    <>
                                                        {shoppingCartPurchaseQty ? (
                                                            <span>
                                                                {
                                                                    shoppingCartPurchaseQty
                                                                }
                                                            </span>
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    color: "#a0a0a0",
                                                                }}
                                                            >
                                                                0
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {!selectedShoppingCartStage ? (
                                                    <span
                                                        style={{
                                                            color: "#a0a0a0",
                                                        }}
                                                    >
                                                        스테이지 선택 필요
                                                    </span>
                                                ) : (
                                                    <CustomSmallButton
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setViewPurchaseModalOpen(
                                                                true,
                                                            );
                                                            setTargetProductOptionDetail(
                                                                productOptionDetail,
                                                            );
                                                        }}
                                                    >
                                                        담기
                                                    </CustomSmallButton>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-2 items-center">
                                                    <CustomSmallButton
                                                        type="button"
                                                        // className='view-memo-button'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setViewPurchaseMemosModalOpen(
                                                                true,
                                                            );
                                                            setTargetProductOptionDetail(
                                                                productOptionDetail,
                                                            );
                                                        }}
                                                    >
                                                        보기
                                                    </CustomSmallButton>
                                                    {defaultPurchaseInfo &&
                                                        defaultPurchaseInfo?.purchaseUrl && (
                                                            <CustomSmallButton
                                                                type="button"
                                                                className="bg-blue-400 hover:bg-blue-500 text-white"
                                                                onClick={() => {
                                                                    window.open(
                                                                        defaultPurchaseInfo?.purchaseUrl,
                                                                        "_blank",
                                                                        "noopener,noreferrer",
                                                                    );
                                                                }}
                                                            >
                                                                <LucideLink
                                                                    size={16}
                                                                />
                                                            </CustomSmallButton>
                                                        )}
                                                </div>
                                                {/* {productOptionDetail?.productDto?.purchaseUri ?
                                                <Link to={productOptionDetail?.productDto?.purchaseUri} target='_blank' rel="noopener noreferrer">구매 링크</Link>
                                                :
                                                <span style={{ color: '#a0a0a0' }}>미등록</span>
                                            } */}
                                            </TableCell>
                                        </TableRow>
                                    );
                                },
                            )}
                        </tbody>
                    </StyledTable>
                </TableContainer>
            </StyledContainer>

            {viewMemoModalOpen && targetProductOptionDetail && (
                <ModalViewMemo
                    open={viewMemoModalOpen}
                    onClose={handleViewMemoModalClose}
                    productOptionDetail={targetProductOptionDetail}
                />
            )}

            {viewPurchaseModalOpen && targetProductOptionDetail && (
                <ModalViewPurchase
                    open={viewPurchaseModalOpen}
                    productOptionDetail={targetProductOptionDetail}
                    SAFETY_STOCK_QTY_MULTIPLIER={SAFETY_STOCK_QTY_MULTIPLIER}
                    onClose={() => {
                        setViewPurchaseModalOpen(false);
                        setTargetProductOptionDetail(null);
                    }}
                />
            )}

            {viewPurchaseMemosModalOpen && targetProductOptionDetail && (
                <ModalViewPurchaseMemo
                    open={viewPurchaseMemosModalOpen}
                    productOptionDetail={targetProductOptionDetail}
                    onClose={() => {
                        setViewPurchaseMemosModalOpen(false);
                        setTargetProductOptionDetail(null);
                    }}
                />
            )}

            {viewProductAnalyticsModalOpen && targetProductOptionDetail && (
                <ModalViewProductAnalytics
                    open={viewProductAnalyticsModalOpen}
                    onClose={handleViewProductAnalyticsModalClose}
                    requestProductOptionDetail={targetProductOptionDetail}
                />
            )}

            {viewAlreadyPurchasedQtyModalOpen &&
                targetAlreadyPurchasedQtyData && (
                    <ModalViewAlreadyPurchasedQty
                        open={viewAlreadyPurchasedQtyModalOpen}
                        onClose={() => {
                            setViewAlreadyPurchasedQtyModalOpen(false);
                            setTargetAlreadyPurchasedQtyData(null);
                        }}
                        targetAlreadyPurchasedQtyData={
                            targetAlreadyPurchasedQtyData
                        }
                    />
                )}

            {customPopoverHook.anchorEl && customPopoverHook.element && (
                <CustomPopover
                    anchorEl={customPopoverHook.anchorEl}
                    onClose={customPopoverHook.closePopover}
                    customLayout={false}
                    layoutStyle={{ width: "fit-content", maxWidth: "400px" }}
                >
                    <OutlierWarningPopoverFragment
                        productOptionDetail={
                            customPopoverHook.element?.productOptionDetail
                        }
                        recommendPurchaseQty={
                            customPopoverHook.element?.recommendPurchaseQty
                        }
                        totalAlreadyPurchasedQty={
                            customPopoverHook.element?.totalAlreadyPurchasedQty
                        }
                    />
                </CustomPopover>
            )}
        </>
    );
}
